import {
  TROPHY_TIER_IDS,
  type GamificationTrophyItem,
  type TrophyId,
} from '@crowdfpl/domain/gamification';
import { Badge } from '@crowdfpl/ui-web/badge';
import { Card } from '@crowdfpl/ui-web/card';
import { Pin, Sparkles, Users } from 'lucide-react';
import { type ReactElement } from 'react';
import {
  formatRarityPercent,
  formatTrophyTitle,
  TROPHY_ACCENT,
  TROPHY_CONDITIONS,
  TROPHY_EMOJI,
  TROPHY_METRIC_UNIT,
  TROPHY_TAGLINE_COPY,
} from '@/components/gamification/trophy-copy';
import { trophyTierToneClass } from '@/components/gamification/trophy-tier-styles';
import { isTrophyUnlocked } from '@/components/gamification/trophy-view-model';
import { trackUmamiEvent } from '@/helpers/analytics';
import { formatGamificationEvidenceSummary } from '@/helpers/gamification-evidence';
import { cn } from '@/helpers/utils';
import { TrophyConditions } from './trophy-conditions';
import { TrophyProgressLadder } from './trophy-progress-ladder';

type TrophyCardProps = {
  trophy: GamificationTrophyItem;
  className?: string;
  isOwnProfile?: boolean;
  pinnedTrophyId?: TrophyId | null;
  onPin?: (trophyId: TrophyId) => void;
  isPinPending?: boolean;
};

function formatTierLabel(tier: string | null): string | null {
  if (!tier) {
    return null;
  }
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

function formatUnlockDate(iso: string | null): string | null {
  if (!iso) {
    return null;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function nextTierLabel(trophy: GamificationTrophyItem): string | null {
  if (trophy.status === 'maxed' || trophy.next_target == null) {
    return null;
  }
  const nextIndex = trophy.highest_tier_index == null ? 0 : trophy.highest_tier_index + 1;
  const tier = TROPHY_TIER_IDS[nextIndex];
  if (!tier) {
    return null;
  }
  const unit = TROPHY_METRIC_UNIT[trophy.trophy_id];
  const requirement = unit ? `${trophy.next_target} ${unit}` : String(trophy.next_target);
  return `Next: ${formatTierLabel(tier)} · ${requirement}`;
}

function closestRunLabel(trophy: GamificationTrophyItem): string | null {
  if (trophy.closest_gameweek == null) {
    return null;
  }
  const unit = TROPHY_METRIC_UNIT[trophy.trophy_id];
  const value = unit ? `${trophy.best_value} ${unit}` : String(trophy.best_value);
  return `Closest: ${value} · GW${trophy.closest_gameweek}`;
}

export function RarityChip({ rarityPct }: { rarityPct: number | null }): ReactElement | null {
  if (rarityPct == null) {
    return null;
  }
  const isRare = rarityPct <= 10;
  const shownPct = formatRarityPercent(rarityPct);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider',
        isRare
          ? 'border border-gamification-rare bg-gamification-rare/10 text-gamification-rare-foreground ring-1 ring-gamification-rare-ring/40'
          : 'bg-muted text-muted-foreground',
      )}
      title={`${shownPct}% of active managers own this trophy`}
      data-testid="trophy-rarity"
      aria-label={isRare ? `Rare: owned by ${shownPct} percent` : `Owned by ${shownPct} percent`}
    >
      <Users className="h-2.5 w-2.5" aria-hidden />
      {shownPct}%
    </span>
  );
}

/** Ladder, next/metric, and unlock evidence — shared with Featured so a pin does not hide progress. */
export function TrophyCardProgress({ trophy }: { trophy: GamificationTrophyItem }): ReactElement {
  const accent = TROPHY_ACCENT[trophy.trophy_id];
  const unlockDate = formatUnlockDate(trophy.first_unlocked_at);
  const evidenceSummary = formatGamificationEvidenceSummary(trophy.evidence);
  const nextLine = nextTierLabel(trophy);
  const closestLine = closestRunLabel(trophy);
  const isMaxed = trophy.status === 'maxed';
  const isLocked = !isTrophyUnlocked(trophy);

  if (trophy.kind === 'multi_tier') {
    return (
      <>
        <TrophyProgressLadder
          highestTierIndex={trophy.highest_tier_index}
          isMaxed={isMaxed}
          nextProgress={trophy.next_progress}
        />
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-[10.5px] text-muted-foreground">
          {nextLine ? (
            <span data-testid="trophy-next-progress" className="tabular-nums">
              {nextLine}
            </span>
          ) : null}
          <span data-testid="trophy-metric" className="tabular-nums">
            {trophy.metric_value}
            {trophy.next_target != null ? ` / ${trophy.next_target}` : ''}
          </span>
          {closestLine ? (
            <span data-testid="trophy-closest-run" className="tabular-nums">
              {closestLine}
            </span>
          ) : null}
        </div>
        {!isLocked && trophy.first_unlocked_gameweek != null ? (
          <p className="text-[11px] text-muted-foreground" data-testid="trophy-first-unlock">
            First unlock · GW{trophy.first_unlocked_gameweek}
            {unlockDate ? ` · ${unlockDate}` : ''}
          </p>
        ) : null}
      </>
    );
  }

  if (!isLocked && (trophy.first_unlocked_gameweek != null || unlockDate)) {
    return (
      <div
        className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[11px]"
        style={{
          background: `color-mix(in srgb, ${accent} 7%, transparent)`,
          boxShadow: `0 0 0 1px color-mix(in srgb, ${accent} 20%, transparent) inset`,
        }}
        data-testid="trophy-unlock-gw"
      >
        <Sparkles className="h-3 w-3 shrink-0" style={{ color: accent }} aria-hidden />
        <span className="truncate font-semibold text-foreground">
          Unlocked
          {trophy.first_unlocked_gameweek != null ? ` · GW${trophy.first_unlocked_gameweek}` : ''}
          {unlockDate ? ` · ${unlockDate}` : ''}
        </span>
      </div>
    );
  }

  return (
    <div className="text-[11px] text-muted-foreground/80">
      {evidenceSummary ? (
        <p data-testid="trophy-one-shot-closest-run">
          <span className="font-semibold text-foreground/70">Closest:</span> {evidenceSummary}
        </p>
      ) : (
        <p data-testid="trophy-one-shot-locked">Not unlocked yet</p>
      )}
    </div>
  );
}

/**
 * Trophy card. Renders API fields only — no client metric/rarity math.
 * Own unlocked cards get an explicit "Pin to profile" button (Lovable parity).
 * Share UI is parked — helper stays in gamification-share-action.
 */
export function TrophyCard({
  trophy,
  className,
  isOwnProfile = false,
  pinnedTrophyId = null,
  onPin,
  isPinPending = false,
}: TrophyCardProps): ReactElement {
  const title = formatTrophyTitle(trophy.trophy_id);
  const tagline = TROPHY_TAGLINE_COPY[trophy.trophy_id];
  const accent = TROPHY_ACCENT[trophy.trophy_id];
  const emoji = TROPHY_EMOJI[trophy.trophy_id];
  const tierLabel = formatTierLabel(trophy.highest_tier ?? trophy.current_tier);
  const tierTone = trophyTierToneClass(trophy.highest_tier ?? trophy.current_tier);
  const conditions = TROPHY_CONDITIONS[trophy.trophy_id];
  const isMaxed = trophy.status === 'maxed';
  const unlocked = isTrophyUnlocked(trophy);
  const isLocked = !unlocked;
  const isPinned = pinnedTrophyId === trophy.trophy_id;
  const canPin = isOwnProfile && unlocked && !isPinned && onPin != null;

  const handlePin = (): void => {
    if (!canPin || isPinPending || !onPin) {
      return;
    }
    trackUmamiEvent('gamification_trophy_pin', { trophy_id: trophy.trophy_id });
    onPin(trophy.trophy_id);
  };

  const handleTrophyClick = (): void => {
    trackUmamiEvent('gamification_trophy_click', {
      trophy_id: trophy.trophy_id,
      status: trophy.status,
    });
  };

  return (
    <Card
      onClick={handleTrophyClick}
      className={cn(
        'relative flex flex-col gap-2 overflow-hidden border-border bg-card p-3',
        trophy.is_new && 'ring-1 ring-gamification-rare-ring/50',
        isPinned && 'border-primary/50',
        isLocked && 'bg-muted/20',
        className,
      )}
      data-testid={`trophy-card-${trophy.trophy_id}`}
      data-status={trophy.status}
      data-kind={trophy.kind}
      data-pinned={isPinned ? 'true' : 'false'}
      data-gamification-motion="decorative"
    >
      <div className="flex items-start gap-2.5">
        <div
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-xl',
            isLocked && 'opacity-60 grayscale',
          )}
          style={{
            background: `color-mix(in srgb, ${accent} 10%, transparent)`,
            boxShadow: isLocked ? 'none' : `0 0 0 1px color-mix(in srgb, ${accent} 25%, transparent) inset`,
          }}
          aria-hidden
        >
          {emoji}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <h3 className="text-[13px] font-bold leading-tight text-foreground">{title}</h3>
            {tierLabel ? (
              <Badge
                variant="outline"
                className={cn('px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider', tierTone)}
                data-testid="trophy-tier-badge"
              >
                {tierLabel}
              </Badge>
            ) : null}
            {trophy.kind === 'one_shot' && !isLocked ? (
              <span
                className="rounded-full bg-foreground/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-foreground"
                data-testid="trophy-unlocked-badge"
              >
                Unlocked
              </span>
            ) : null}
            {isLocked ? (
              <Badge variant="outline" data-testid="trophy-locked-badge">
                Locked
              </Badge>
            ) : null}
            {trophy.is_new ? (
              <Badge className="bg-primary text-primary-foreground" data-testid="trophy-new-badge">
                New
              </Badge>
            ) : null}
            {isMaxed ? (
              <Badge variant="secondary" data-testid="trophy-max-badge">
                Max
              </Badge>
            ) : null}
            {isPinned ? (
              <Badge variant="secondary" data-testid="trophy-pinned-badge">
                <Pin className="mr-1 h-3 w-3" aria-hidden />
                Pinned
              </Badge>
            ) : null}
            <RarityChip rarityPct={trophy.rarity_pct} />
          </div>
          <p
            className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-muted-foreground"
            data-testid="trophy-tagline"
          >
            {tagline}
          </p>
        </div>
      </div>

      <TrophyCardProgress trophy={trophy} />

      <TrophyConditions conditions={conditions} trophyId={trophy.trophy_id} />

      {canPin ? (
        <button
          type="button"
          className="mt-auto inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 text-[11px] font-semibold transition-colors hover:bg-muted disabled:opacity-50"
          onClick={handlePin}
          disabled={isPinPending}
          data-testid="trophy-pin"
        >
          <Pin className="h-3 w-3" aria-hidden />
          Pin to profile
        </button>
      ) : null}
    </Card>
  );
}
